import { SyntheticEvent, useMemo, useState } from 'react';

import { Typography } from '@material-ui/core';
import {
  Button,
  Dialog,
  DialogContent as DialogBody,
  DialogActions as DialogFooter,
  DialogTitle as DialogHeader,
  IconButton,
  Typography as Text,
  TextField,
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';

import { RiCloseLine } from '@remixicon/react';

import { getErrorMessage } from '@spotify/confidence-core-react';
import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';

import {
  useCanCreatePulse,
  useCapturePulseBlock,
  useCreatePulse,
  usePulses,
} from '../../hooks';
import {
  PULSE_BLOCK_TYPE,
  type PulseListItem,
  type SaveToPulseInput,
} from '../../types';
import { getPulseTitle, stripPulseVisualizationResultData } from '../../utils';
import { usePulseBlockCaptures } from '../PulseBlockCaptures';

import classes from './SaveToPulseDialog.module.css';

export type SaveToPulseSuccess = {
  pulseName: string;
  pulseDisplayName: string;
};

type SaveToPulseDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  input: SaveToPulseInput;
  onSuccess?: (info: SaveToPulseSuccess) => void;
};

type PulseOption =
  | {
      type: 'existing';
      value: string;
      label: string;
    }
  | {
      type: 'create';
      inputValue: string;
      label: string;
    };

const filterPulseOptions = createFilterOptions<PulseOption>();

const getPulseOptionLabel = (option: PulseOption | string): string => {
  if (typeof option === 'string') {
    return option;
  }

  if (option.type === 'create') {
    return option.inputValue;
  }

  return option.label;
};

const PulseLoadingState = () => (
  <Text variant="body2" color="textSecondary">
    Loading pulses...
  </Text>
);

export const SaveToPulseDialog = ({
  isOpen,
  onOpenChange,
  input,
  onSuccess,
}: SaveToPulseDialogProps) => {
  const { pulses, isLoading } = usePulses();
  const { capturePulseBlock, isMutating } = useCapturePulseBlock();
  const { createPulse, isCreating } = useCreatePulse();
  const { addCapture, projectName } = usePulseBlockCaptures();
  const { allowed: canCreatePulse } = useCanCreatePulse();

  const [blockName, setBlockName] = useState(input.suggestedName);
  const [pulseInputValue, setPulseInputValue] = useState('');
  const [createdPulseName, setCreatedPulseName] = useState<string | null>(null);
  const [selectedPulseName, setSelectedPulseName] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const pulseOptions = useMemo<PulseOption[]>(
    () =>
      (pulses ?? []).map(pulse => ({
        type: 'existing',
        value: pulse.name,
        label: getPulseTitle(pulse),
      })),
    [pulses],
  );

  const selectedPulse = useMemo<PulseListItem | null>(
    () =>
      (pulses ?? []).find(pulse => pulse.name === selectedPulseName) ?? null,
    [pulses, selectedPulseName],
  );
  const { allowed: canEditSelectedPulse, loading: isLoadingSelectedPulseEdit } =
    useCheckResourcePermissions({
      name: selectedPulseName ?? undefined,
      can: 'edit',
      optimistic: false,
    });

  const isSaving = isMutating || isCreating;

  const resetAndClose = () => {
    setErrorMessage(null);
    onOpenChange(false);
  };

  const getBlockDisplayName = () => blockName.trim() || input.suggestedName;

  const getCaptureVisualizationSpec = () =>
    input.blockType === PULSE_BLOCK_TYPE.CHART
      ? stripPulseVisualizationResultData(input.visualizationSpec)
      : input.visualizationSpec;

  const newPulseDisplayName = pulseInputValue.trim();

  const handleAddToPulse = async () => {
    if (!selectedPulse && !newPulseDisplayName && !createdPulseName) return;
    setErrorMessage(null);
    try {
      const pulseName =
        selectedPulse?.name ??
        createdPulseName ??
        (
          await createPulse(
            {
              displayName: newPulseDisplayName,
              ...(projectName ? { projects: [projectName] } : {}),
            },
            undefined,
          )
        ).name;
      const pulseDisplayName =
        selectedPulse?.displayName ?? newPulseDisplayName;

      if (!selectedPulse) {
        setCreatedPulseName(pulseName);
      }

      const result = await capturePulseBlock({
        sourceChat: input.sourceChat,
        sourceEvent: input.sourceEvent,
        displayName: getBlockDisplayName(),
        blockType: input.blockType,
        visualizationSpec: getCaptureVisualizationSpec(),
        selectedContent: input.selectedContent,
        pulse: pulseName,
      });
      if (result.block?.name) {
        addCapture({
          pulseName,
          pulseDisplayName,
          blockName: result.block.name,
          blockDisplayName: result.block.displayName || getBlockDisplayName(),
        });
      }
      onSuccess?.({ pulseName, pulseDisplayName });
      resetAndClose();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const handlePulseSelectionChange = (
    _event: SyntheticEvent<EventTarget>,
    option: PulseOption | string | null,
  ) => {
    if (typeof option === 'string') {
      setSelectedPulseName(null);
      setPulseInputValue(option);
    } else if (option?.type === 'create') {
      setSelectedPulseName(null);
      setPulseInputValue(option.inputValue);
    } else {
      setSelectedPulseName(option?.value ?? null);
      setPulseInputValue(option?.label ?? '');
    }
    setCreatedPulseName(null);
  };

  const handlePulseInputChange = (
    _event: SyntheticEvent<EventTarget>,
    value: string,
    reason: 'input' | 'reset' | 'clear',
  ) => {
    setPulseInputValue(value);
    if (reason === 'input' || reason === 'clear') {
      setSelectedPulseName(null);
      setCreatedPulseName(null);
    }
  };

  const hasBlockName = getBlockDisplayName().length > 0;
  const hasValidPulseTarget = selectedPulse
    ? canEditSelectedPulse
    : canCreatePulse && newPulseDisplayName.length > 0;
  const canAddToPulse = hasValidPulseTarget && hasBlockName && !isSaving;
  const isCreatingNewPulse =
    selectedPulse === null && newPulseDisplayName.length > 0;
  let addButtonLabel = 'Add to pulse';
  if (isSaving) {
    addButtonLabel = 'Adding...';
  } else if (isCreatingNewPulse && !createdPulseName) {
    addButtonLabel = 'Create pulse and add';
  }

  let pulseHelperText: string | undefined;
  if (canCreatePulse) {
    pulseHelperText = isCreatingNewPulse
      ? `A new pulse named "${newPulseDisplayName}" will be created.`
      : 'Select an existing pulse or enter a name to create a new one.';
  }

  const pulseSelector = isLoading ? (
    <PulseLoadingState />
  ) : (
    <Autocomplete<PulseOption, false, false, true>
      freeSolo
      options={pulseOptions}
      getOptionLabel={getPulseOptionLabel}
      getOptionSelected={(option, value) =>
        typeof value !== 'string' &&
        option.type === 'existing' &&
        value.type === 'existing' &&
        option.value === value.value
      }
      filterOptions={(options, params) => {
        const filtered = filterPulseOptions(options, params);
        const inputValue = params.inputValue.trim();
        const hasExactMatch = options.some(
          option =>
            option.type === 'existing' &&
            option.label.toLocaleLowerCase() === inputValue.toLocaleLowerCase(),
        );
        if (canCreatePulse && inputValue && !hasExactMatch) {
          filtered.push({
            type: 'create',
            inputValue,
            label: `Create "${inputValue}"`,
          });
        }
        return filtered;
      }}
      value={
        pulseOptions.find(
          option =>
            option.type === 'existing' && option.value === selectedPulseName,
        ) ?? null
      }
      inputValue={pulseInputValue}
      onChange={handlePulseSelectionChange}
      onInputChange={handlePulseInputChange}
      openOnFocus
      renderOption={option => option.label}
      renderInput={params => (
        <TextField
          {...params}
          label={canCreatePulse ? 'Choose or create a pulse' : 'Choose a pulse'}
          placeholder={
            canCreatePulse
              ? 'Search pulses or enter a new name'
              : 'Search pulses'
          }
          helperText={pulseHelperText}
        />
      )}
    />
  );

  return (
    <Dialog open={isOpen} onClose={() => onOpenChange(false)}>
      <DialogHeader>
        Add to pulse
        <IconButton
          aria-label="Close"
          onClick={() => onOpenChange(false)}
          style={{ position: 'absolute', right: 8, top: 8 }}
        >
          <RiCloseLine size={16} />
        </IconButton>
      </DialogHeader>
      <DialogBody className={classes.body}>
        <div className={classes.form}>
          <TextField
            id="save-to-pulse-block-name"
            label="Block name"
            value={blockName}
            onChange={e => setBlockName(e.target.value)}
            required
            error={!hasBlockName}
          />

          {input.selectedContent && (
            <div>
              <Typography variant="caption" color="textSecondary">
                Selected content
              </Typography>
              <div className={classes.selectedContentPreview}>
                <Typography
                  variant="body2"
                  className={classes.selectedContentText}
                >
                  {input.selectedContent}
                </Typography>
              </div>
            </div>
          )}

          {pulseSelector}

          {selectedPulse &&
            !isLoadingSelectedPulseEdit &&
            !canEditSelectedPulse && (
              <Text
                component="p"
                variant="body2"
                color="textSecondary"
                className={classes.helpText}
              >
                You need edit access to add a block to this pulse.
              </Text>
            )}

          {!isLoading && !canCreatePulse && pulseOptions.length === 0 && (
            <Text
              component="p"
              variant="body2"
              color="textSecondary"
              className={classes.helpText}
            >
              No pulses are available.
            </Text>
          )}

          {errorMessage && (
            <Text
              component="p"
              variant="body2"
              color="error"
              className={classes.helpText}
            >
              {errorMessage}
            </Text>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outlined" onClick={resetAndClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleAddToPulse()}
          disabled={!canAddToPulse}
          color="primary"
        >
          {addButtonLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
